(function youtubePlayer(global) {
  'use strict';

  global.NewHomeSource = global.NewHomeSource || {};
  global.NewHomeSource.VideoPlayers = global.NewHomeSource.VideoPlayers || {};

  class Youtube {
    /**
     * Initializes a new instance of the YouTube video player handler.
     */
    constructor() {
      this.youtubeAPI = '//www.youtube.com/iframe_api';
      this.videoId = null;
      this.eventsHandler = null;
      this.player = null;
      this.loadSampleRate = 100;
      this.attempts = 10;
      this.isPlaying = false;
      this.defaultEvents = {
        onEnded: function onEnded() { },
        onPlaying: function onPlaying() { },
        onPaused: function onPaused() { },
        onLoaded: function onLoaded() { },
      };
    }

    /**
    * @param {string} source id of the video to be displayed.
    * @param {object} config with the extra information needed by the player.
    */
    initialize(source, config) {
      this.videoId = source;
      this.eventsHandler = Object.assign({ }, this.defaultEvents, config);
    }

    /**
   * Appends a YouTube API script to the HTML document and handles loading and error events.
   * @param resolve -  is called when the asynchronous operation is successful
   * and the promise is fulfilled. It is typically used to pass the result of the operation to the
   * promise chain.
   * @param reject - is a function that is called when an error occurs during the
   * loading of the YouTube script. It is typically used to handle the error and perform any necessary
   * cleanup or error handling tasks.
   */
    appendYoutubeScript(resolve, reject) {
      const tag = document.getElementsByTagName('meta')[0];
      const script = document.createElement('script');

      script.type = 'text/javascript';
      script.async = true;
      script.src = this.youtubeAPI;
      script.onerror = Youtube.onYoutubeError.bind(this, reject);
      window.onYouTubeIframeAPIReady = Youtube.dispatchYoutubeLoadedEvent;
      document.body.addEventListener('youtubeLoaded', Youtube.onYoutubeLoaded.bind(this, resolve));
      tag.parentNode.insertBefore(script, tag);
    }

    /**
   * If the youtube is loaded, resolve the promise.
   * If not, wait the amount of time from loadSampleRate and try again.
   * @param resolve - The resolve function of the promise.
   */
    isYoutubeLoaded(resolve, reject) {
      if (!this.attempts) {
        reject();
        return;
      }

      if (Youtube.loadingStatus === 'Succeeded') {
        resolve(window.YT);
      } else {
        this.attempts -= 1;
        setTimeout(this.isYoutubeLoaded.bind(this, resolve, reject), this.loadSampleRate);
      }
    }

    /**
   * Wait the amount of time from loadSampleRate for Youtube to load, and then resolve the promise.
   * @returns A promise that will resolve when the Youtube api is loaded.
   */
    waitForYoutubeScript() {
      return new Promise((resolve, reject) => {
        setTimeout(this.isYoutubeLoaded.bind(this, resolve, reject), this.loadSampleRate);
      });
    }

    /**
     * Loads the YouTube API script if it hasn't been loaded yet.
     * - If the script is idle, sets the loading status and appends the script.
     * - If already loaded, resolves immediately with the YouTube API object.
     * - If loading is in progress, waits for the script to finish loading.
     *
     * @returns {Promise<object>} A promise that resolves with the YouTube API object (window.YT).
     */
    loadYoutubeApiScript() {
      if (Youtube.loadingStatus === 'Idle') {
      // Set the Youtube instance as loading to prevent multiple requests.
        Youtube.loadingStatus = 'Loading';
        return new Promise(this.appendYoutubeScript.bind(this));
      }

      if (Youtube.loadingStatus === 'Succeeded') {
      // If the Youtube instance is already loaded resolve the promise.
        return Promise.resolve(window.YT);
      }

      // Otherwise wait to review if the Youtube instance has loaded.
      return this.waitForYoutubeScript();
    }

    /**
     * Handles the YouTube player ready event.
     * Resolves the provided promise and optionally starts playback if `playOnLoaded` is true.
     *
     * @param {Function} resolve - The function to call to resolve the promise when the player is ready.
     */
    onReady(resolve) {
      resolve();
      if (this.eventsHandler.playOnLoaded) {
        this.play();
      }
    }

    /**
     * Handles state changes from the YouTube player and triggers corresponding event handlers.
     *
     * @param {Object} event - The event object from the YouTube player.
     * @param {number} event.data - The state of the player (0: ended, 1: playing, 2: paused, etc.).
     * @returns {void}
     */
    onPlayerStateChange(event) {
      switch (event.data) {
        case 0:
        // Ended.
          this.isPlaying = false;
          document.body.dispatchEvent(new CustomEvent('videoInteraction', {
            detail: { originator: 'youtube', interaction: 'video_completed' }
          }));
          this.eventsHandler.onEnded(event);
          break;
        case 1:
        // Playing.
          // Only send play event if not already playing.
          if (!this.isPlaying) {
            this.isPlaying = true;
            document.body.dispatchEvent(new CustomEvent('videoInteraction', {
              detail: { originator: 'youtube', interaction: 'video_play' }
            }));
          }
          this.eventsHandler.onPlaying(event);
          break;
        case 2:
        // Paused.
          this.isPlaying = false;
          document.body.dispatchEvent(new CustomEvent('videoInteraction', {
            detail: { originator: 'youtube', interaction: 'video_pause' }
          }));
          this.eventsHandler.onPaused(event);
          break;
        default:
      }
    }

    /**
    * Creates a new YouTube player instance with a specified video ID, and returns the player object.
    */
    async createVideo() {
      this.youtubeSdk = await this.loadYoutubeApiScript();
      await new Promise((resolve) => {
        this.player = new this.youtubeSdk.Player(this.videoId, {
          videoId: this.videoId,
          height: '100%',
          width: '100%',
          events: {
            onStateChange: this.onPlayerStateChange.bind(this),
            onReady: this.onReady.bind(this, resolve),
          },
        });
      });
    }

    /**
    * Destroy the video reference for youtube.
    *
    */
    dispose() {
      if (!this.player) {
        return;
      }

      this.player.destroy();
    }

    /**
    * Pauses the youtube video.
    *
    */
    pause() {
      if (!this.player) {
        return;
      }

      this.player.pauseVideo();
    }

    /**
    * Plays the youtube video.
    *
    */
    play() {
      if (!this.player) {
        return;
      }

      this.player.playVideo();
    }

    /**
    * When the Youtube API fail, set the isYoutubeLoaded and isYoutubeLoading properties of the instance object to
    * false and reject the promise.
    * @param resolve - A function that is called when Youtube API fails.
    */
    static onYoutubeError(reject) {
      Youtube.loadingStatus = 'Failed';
      reject(new Error('Unable to load Youtube API.'));
    }

    /**
    * When the Youtube API is loaded, set the isYoutubeLoaded property of the instance object to
    * true and resolve the promise.
    * @param resolve - A function that is called when the promise is resolved.
    */
    static onYoutubeLoaded(resolve) {
      Youtube.loadingStatus = 'Succeeded';
      resolve(window.YT);
    }

    /**
    * Creates an HTML element needed by the Youtube JavaScript to create the player.
    */
    static createContainer(videoId) {
      const videoElement = document.createElement('div');
      videoElement.id = videoId;
      videoElement.style.width = '100%';
      videoElement.style.height = '100%';
      videoElement.innerHTML = '';
      return videoElement;
    }

    /**
    * Function dispatches a custom event named 'youtubeLoaded' on the document body
    * when the YouTube SDK is loaded.
    */
    static dispatchYoutubeLoadedEvent() {
      document.body.dispatchEvent(new CustomEvent('youtubeLoaded'));
    }
  }

  Object.defineProperty(Youtube, 'loadingStatus', { value: 'Idle', writable: true });

  global.NewHomeSource.VideoPlayers.Youtube = Youtube;
}(window));
